import type { Currency } from '../../shared/invoice.ts';
import {
  computeInvoiceTaxCents,
  computeInvoiceTotalCents,
  computeLineAmountCents,
  formatCurrencyCents,
} from '../../shared/format.ts';

export interface InvoiceTotalsItem {
  quantity: string;
  rate: string;
}

export interface InvoiceTotalsProps {
  currency: Currency;
  items: InvoiceTotalsItem[];
  taxRate: string;
  taxLabel: string;
  readOnly: boolean;
  onTaxRateChange: (value: string) => void;
  onTaxLabelChange: (value: string) => void;
}

// Mirrors the server's TaxRateSchema in src/shared/invoice.ts: accepts canonical
// decimals 0–100 with up to two fractional digits. Live UI uses the same cap so
// the displayed tax never previews a value the save will reject.
const RATE_INPUT_PATTERN = /^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/;
const MAX_TAX_RATE_PERCENT = 100;

export function InvoiceTotals({
  currency,
  items,
  taxRate,
  taxLabel,
  readOnly,
  onTaxRateChange,
  onTaxLabelChange,
}: InvoiceTotalsProps): React.ReactElement {
  const subtotalCents = safeInvoiceTotal(items);
  const formattedSubtotal = formatCurrencyCents(subtotalCents, currency);
  const taxCents = safeTaxCents(subtotalCents, taxRate);
  const formattedTax = formatCurrencyCents(taxCents, currency);
  const totalCents = subtotalCents + taxCents;
  const formattedTotal = formatCurrencyCents(totalCents, currency);
  const ratePercent = displayRatePercent(taxRate);

  return (
    <section aria-label="Invoice totals" className="border-t border-line pt-5">
      <dl className="ml-auto max-w-xs space-y-2 text-sm">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted">
            Subtotal
          </dt>
          <dd className="font-serif text-base tabular-nums text-ink">{formattedSubtotal}</dd>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex min-w-0 flex-1 basis-full items-center gap-2 sm:basis-auto">
            <input
              type="text"
              aria-label="Tax label"
              placeholder="Tax"
              value={taxLabel}
              disabled={readOnly}
              maxLength={60}
              onChange={(event) => onTaxLabelChange(event.currentTarget.value)}
              className="min-h-11 min-w-0 flex-1 rounded-md border border-line bg-paper-soft px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted outline-none transition placeholder:text-ink-muted/70 focus:border-ochre focus:ring-2 focus:ring-ochre/30 disabled:cursor-not-allowed disabled:bg-paper-deep"
            />
            <div className="flex shrink-0 items-center gap-1">
              <input
                type="text"
                inputMode="decimal"
                aria-label="Tax rate percent"
                placeholder="0"
                value={taxRate}
                disabled={readOnly}
                maxLength={6}
                onChange={(event) => onTaxRateChange(event.currentTarget.value)}
                className="min-h-11 w-16 rounded-md border border-line bg-paper-soft px-2 py-1 text-right text-sm tabular-nums text-ink outline-none transition focus:border-ochre focus:ring-2 focus:ring-ochre/30 disabled:cursor-not-allowed disabled:bg-paper-deep"
              />
              <span className="text-sm tabular-nums text-ink-muted">%</span>
            </div>
          </div>
          <dd className="font-serif text-base tabular-nums text-ink-soft">{formattedTax}</dd>
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted/80">
          {ratePercent ? `Tax = subtotal × ${ratePercent}%` : 'Tax disabled at 0%'}
        </p>
        <div className="mt-2 flex items-baseline justify-between gap-4 rounded-xl border border-line bg-paper-deep/60 px-4 py-3">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink">Total</dt>
          <dd className="font-serif text-2xl tabular-nums text-ink">{formattedTotal}</dd>
        </div>
      </dl>
    </section>
  );
}

function safeLineAmountCents(item: InvoiceTotalsItem): number {
  try {
    return computeLineAmountCents(item);
  } catch {
    return 0;
  }
}

function safeInvoiceTotal(items: InvoiceTotalsItem[]): number {
  try {
    return computeInvoiceTotalCents(items);
  } catch {
    return items.reduce((total, item) => total + safeLineAmountCents(item), 0);
  }
}

function safeTaxCents(subtotalCents: number, taxRate: string): number {
  const canonical = canonicalizeRate(taxRate);
  if (canonical === null) return 0;
  try {
    return computeInvoiceTaxCents(subtotalCents, canonical);
  } catch {
    return 0;
  }
}

function canonicalizeRate(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return '0';
  if (!RATE_INPUT_PATTERN.test(trimmed)) return null;
  return Number.parseFloat(trimmed) <= MAX_TAX_RATE_PERCENT ? trimmed : null;
}

function displayRatePercent(value: string): string | null {
  const canonical = canonicalizeRate(value);
  if (canonical === null) return null;
  if (canonical === '0' || /^0(?:\.0+)?$/.test(canonical)) return null;
  return canonical;
}
